/**
 * Player preferences (per browser). Read by the renderer, effects and audio every frame.
 *
 * Each setting is declared by its feature in a `*.setting.ts` file (see settingsRegistry.ts); all
 * of them are loaded here, before the saved settings are read, so `settings` always holds every one.
 */
import { type SettingDef, settingDefs, type SettingGroup, type Settings } from './settingsRegistry';

// Every declared setting (`*.setting.ts` anywhere in the client), registered before they are loaded.
import.meta.glob('./**/*.setting.ts', { eager: true });

export type { Settings };

const KEY = 'nine-swords.settings.v1';

/** The declared defaults, in order: the saved JSON keeps their keys in this order. */
function declaredDefaults(): Settings {
  const out: Record<string, unknown> = {};
  for (const d of settingDefs()) out[d.key] = d.default;
  return out as unknown as Settings;
}
const DEFAULTS: Settings = declaredDefaults();

function load(): Settings {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Settings>;
    const out = { ...DEFAULTS };
    for (const k of Object.keys(DEFAULTS) as (keyof Settings)[])
      if (typeof raw[k] === typeof DEFAULTS[k]) (out as Record<string, unknown>)[k] = raw[k];
    for (const d of settingDefs())
      if (d.range) {
        const n = out as unknown as Record<string, number>;
        n[d.key] = Math.min(d.range[1], Math.max(d.range[0], n[d.key]!));
      }
    return out;
  } catch {
    return { ...DEFAULTS };
  }
}

export const settings: Settings = load();

export function saveSettings(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Storage blocked: settings last for this session only.
  }
}

const resetHooks: (() => void)[] = [];
/** Runs `f` whenever the settings are put back to their defaults (before they are saved). */
export function onSettingsReset(f: () => void): void {
  resetHooks.push(f);
}

export function resetSettings(): void {
  Object.assign(settings, DEFAULTS);
  for (const f of resetHooks) f();
  saveSettings();
}

/** A true/false setting. */
export type Toggle = { [K in keyof Settings]: Settings[K] extends boolean ? K : never }[keyof Settings];

/** The settings menu's checkboxes in a section, in order: each setting's key and Thai label. */
export function menuToggles(group: SettingGroup): [Toggle, string][] {
  return settingDefs().flatMap((d) =>
    d.menu?.group === group && !d.menu.choices ? [[d.key as Toggle, d.menu.label] as [Toggle, string]] : [],
  );
}

/** The settings menu's rows in a section, in order: checkboxes and drop-downs (`menu.choices`). */
export function menuRows(group: SettingGroup): readonly SettingDef[] {
  return settingDefs().filter((d) => d.menu?.group === group);
}
